import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Account } from './accountType';
import AccountApi from './accountApi';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import { Edit, Upload, FolderUp, X, Eye, EyeOff } from 'lucide-react';
import PhotoApi from '../photo/photoApi';
import HelpIcon from '../components/ui/helpIcon';
import { validatePasswordMatch, validatePassword } from '../lib/validator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';

const AccountSettingPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Account', path: '/main/account' },
    { label: 'Account Settings', path: '/main/account/settings' },
  ];

  const MAX_PHOTO_UPLOADED_PER_ACCOUNT = 1;
  const [account, setAccount] = useState<Account | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [fileError, setFileError] = useState<string>('');
  const [photoCount, setPhotoCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [photoLink, setPhotoLink] = useState<string>('');
  const [accountPhotoId, setAccountPhotoId] = useState<string>('');
  const [file, setFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    updatePhotoCount();
  }, [account]);

  useEffect(() => {
    const fetchPhotoLink = async () => {
      if (account?.account_id) {
        try {
          const links = await PhotoApi.getPhotoLinks('account', 'small', account.account_id);
          if (links && links.length > 0) {
            setPhotoLink(links[0].url);
            setAccountPhotoId(links[0].id);
          }
        } catch (error) {
          // Error handling
        }
      }
    };

    fetchPhotoLink();
  }, [account, photoCount]);

  useEffect(() => {
    const fetchAccount = async () => {
      const accountId = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      if (!accountId) {
        setErrorMessage('Account ID not available.');
        setLoading(false);
        return;
      }

      try {
        const response = await AccountApi.getAccount(accountId);
        setAccount(response);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };

    fetchAccount();
  }, []);

  const updatePhotoCount = async () => {
    if (account?.account_id) {
      try {
        const count = await PhotoApi.getPhotoCount('account', account.account_id);
        setPhotoCount(count);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!account?.account_id) return;

    try {
      setLoading(true);
      await PhotoApi.delete(accountPhotoId);
      setPhotoLink('');
      await updatePhotoCount();
      setMessage('Photo deleted successfully!');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error deleting photo');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setFile(null);
        return;
      }

      setFileError('');
      setFile(selectedFile);
    }
  };

  const handleUpload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setErrorMessage('');
    setMessage(null);

    if (!file) {
      setErrorMessage('Please select a file to upload');
      return;
    }

    if (!account) {
      setErrorMessage('Account data not found');
      return;
    }

    if (!account.account_id) {
      setErrorMessage('Account Id not found');
      return;
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }

    try {
      setLoading(true);
      const currentCount = await PhotoApi.getPhotoCount('account', account.account_id);
      if (currentCount >= MAX_PHOTO_UPLOADED_PER_ACCOUNT) {
        setErrorMessage(`Maximum number of photos (${MAX_PHOTO_UPLOADED_PER_ACCOUNT}) has been reached for this project`);
        return;
      }

      const response = await PhotoApi.upload(file, {
        account_id: parseInt(account.account_id),
        image_for_tag: 'account',
        image_size_tag: 'small',
      });

      setMessage('Photo uploaded successfully!');
      setFile(null);
      await updatePhotoCount();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error uploading photo');
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }
    try {
      await AccountApi.changePassword(formData.currentPassword, formData.newPassword, formData.confirmPassword);
      setMessage('Password changed successfully. Please logout and re-login');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');

    const passwordResult = validatePassword(formData.newPassword);
    if (!passwordResult.isValid) {
      setErrorMessage(passwordResult.error || 'Invalid password');
      return false;
    }

    const confirmResult = validatePasswordMatch(formData.newPassword, formData.confirmPassword);
    if (!confirmResult.isValid) {
      setErrorMessage(confirmResult.error || 'Password not match');
      return false;
    }

    return true;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'newPassword':
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) {
          errors.push(passwordResult.error || 'Invalid password');
        }
        break;

      case 'confirmPassword':
        const confirmResult = validatePasswordMatch(formData.newPassword, value);
        if (!confirmResult.isValid) {
          errors.push(confirmResult.error || 'Password not match');
        }
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Account Settings</h2>

        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
        {message && <Alert variant="success">{message}</Alert>}

        <fieldset className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <legend className="text-lg font-semibold px-2">Account Information</legend>

        <div className="space-y-3 mb-6">
          <div className="text-base">
            <span className="font-semibold">Name:</span> {account?.first_name} {account?.last_name}
            <span className="ml-4 font-semibold">Phone:</span> {account?.phone || 'N/A'}
          </div>
          <div className="text-base">
            <span className="font-semibold">Email:</span> {account?.email}
          </div>
          <div className="text-base">
            <span className="font-semibold">Login ID:</span> {account?.login_id}
          </div>
        </div>
</fieldset>
        <form onSubmit={handleSubmit}>
          {account?.auth_provider === 'self' && (
            <fieldset className="border-2 border-gray-300 rounded-lg p-6 mb-6">
              <legend className="text-lg font-semibold px-2">Change Password</legend>

              <div className="space-y-4">
                {/* Current Password */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="currentPassword">Current Password:</Label>
                    <span className="text-red-600 font-semibold">*</span>
                  </div>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Label htmlFor="newPassword">New Password:</Label>
                      <span className="text-red-600 font-semibold">*</span>
                    </div>
                    <HelpIcon message="Password must be at least 8 characters long, including both lowercase and uppercase letters, at least one number, and at least one special character" />
                  </div>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="confirmPassword">Confirm New Password:</Label>
                    <span className="text-red-600 font-semibold">*</span>
                  </div>
                  <Input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>

                <div className="pt-2">
                  <Button type="submit" size="medium">
                    <Edit size={16} />
                    Submit
                  </Button>
                </div>
              </div>
            </fieldset>
          )}
        </form>

        <fieldset className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <legend className="text-lg font-semibold px-2">Upload Your Profile Image</legend>

          <div className="mb-6 text-sm text-gray-600">
            Profile image: only {MAX_PHOTO_UPLOADED_PER_ACCOUNT} image is allowed. You can delete it by clicking
            the X at the bottom right corner, and upload a different logo image.
          </div>

          {photoLink && (
            <div className="relative inline-block mb-6">
              <img
                src={photoLink}
                alt="Profile image"
                className="h-[50px] w-auto object-contain"
              />
              <button
                onClick={handleDeletePhoto}
                className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white border border-gray-300 hover:bg-gray-50 cursor-pointer flex items-center justify-center p-0 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {(!photoLink || photoCount < MAX_PHOTO_UPLOADED_PER_ACCOUNT) && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button type="button" size="medium" onClick={handleBrowseClick}>
                  <FolderUp size={18} />
                  Browse
                </Button>
                {file && <span className="text-sm text-gray-600">{file.name}</span>}
              </div>
              {fileError && <Alert variant="error">{fileError}</Alert>}

              <div>
                <Button type="button" size="medium" onClick={handleUpload} disabled={loading}>
                  <Upload size={18} />
                  {loading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      </div>
    </>
  );
};

export default AccountSettingPage;